import { useMemo } from "react"
import { ConditionGroup, CreateProspectPayload, NextPageParams, PaginationMetadata, Prospect } from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type { CreateOpportunityParams } from "@/hooks/api/opportunities/api"
import { useApiContext } from "@/hooks/api/use-api-context"

import {
  AllProspectIdsResponse,
  createProspect,
  fetchAllProspectIds,
  fetchProspect,
  fetchProspects,
  ProspectsResponse,
  updateProspect,
  UpdateProspectPayload,
} from "./api"

export function useFetchProspects(searchTerm?: string) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["prospects", searchTerm],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchProspects(context, undefined, undefined, searchTerm)
    },
  })
}

export function useFetchProspect(id: string | null) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["prospect", id],
    enabled: !!id,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      if (!id) throw new Error("No id found")
      return fetchProspect(context, id)
    },
  })
}

export function useInfiniteProspects() {
  const getContext = useApiContext()
  const { completedConditions, searchTerm, viewSort } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () => ({ combinator: "and", conditions: completedConditions }) as ConditionGroup,
    [completedConditions]
  )

  return useInfiniteQuery<
    ProspectsResponse,
    Error,
    { prospects: Prospect[]; pagination: PaginationMetadata },
    (string | ConditionGroup | undefined | { field?: string; direction?: "asc" | "desc" })[],
    NextPageParams | undefined
  >({
    queryKey: ["prospects", "infinite", searchTerm, computedFilterGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchProspects(context, computedFilterGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      prospects: data.pages.flatMap((page) => page.prospects),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

interface CreateProspectMutationParams {
  prospect: CreateProspectPayload
  overrideBookId?: string
  opportunity?: CreateOpportunityParams & { pipeline_id: string }
}

export function useCreateProspect() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ prospect, overrideBookId, opportunity }: CreateProspectMutationParams) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return createProspect(context, prospect, overrideBookId, opportunity)
    },
    onSuccess: (_prospect, { opportunity }) => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] })

      if (opportunity) {
        queryClient.invalidateQueries({ queryKey: ["pipeline", opportunity.pipeline_id, "opportunities"] })
        queryClient.invalidateQueries({ queryKey: ["household"] })
      }
    },
  })
}

export function useUpdateProspect(id: string) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation<Prospect, Error, UpdateProspectPayload>({
    mutationFn: async (params) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateProspect(context, id, params)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] })
    },
  })
}

export function useFetchAllProspectIds() {
  const getContext = useApiContext()
  const { completedConditions, searchTerm } = useDataTableStore()
  const computedFilterGroup = useMemo(
    () => ({ combinator: "and", conditions: completedConditions }) as ConditionGroup,
    [completedConditions]
  )

  return useQuery<AllProspectIdsResponse, Error>({
    queryKey: ["prospects", "all-ids", searchTerm, computedFilterGroup],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchAllProspectIds(context, computedFilterGroup, searchTerm)
    },
  })
}
